import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  X, Send, User, Loader2, Target, 
  RefreshCw, TrendingUp, Copy, Check,
  Brain, Layers, GitCompare, Sparkles,
  ArrowUpRight, Maximize2, Minimize2, CornerDownLeft,
  Crown, ChevronRight, Lock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const QUICK_ACTIONS = {
  improve: { icon: TrendingUp, label: 'Ulepsz', prompt: 'Przeanalizuj to co napisalem i pokaz ulepszona wersje. Wyjasnij konkretnie co zmieniles i dlaczego to dziala lepiej.' },
  variants: { icon: Layers, label: 'Warianty', prompt: 'Pokaz 3 rozne warianty tego podejscia. Kazdy z innej perspektywy — wyjasnij czym sie roznia.' },
  compare: { icon: GitCompare, label: 'Porównaj', prompt: 'Pokaz porownanie: wersja slaba vs dobra vs premium. Co sprawia ze kazda kolejna jest lepsza?' },
  simplify: { icon: Minimize2, label: 'Uprość', prompt: 'Uprość to podejście. Pokaż jak osiągnąć ten sam efekt w bardziej elegancki sposób.' },
  expand: { icon: Maximize2, label: 'Rozbuduj', prompt: 'Rozbuduj to podejście — dodaj głębię, szczegóły i konkretne przykłady.' },
  nextStep: { icon: ArrowUpRight, label: 'Dalej', prompt: 'Jaki powinien być następny krok? Poprowadź mnie dalej.' },
};

const FormatMessage = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');
  
  return lines.map((line, idx) => {
    let html = line
      .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
      .replace(/`([^`]+)`/g, '<code class="bg-white/10 text-sky-300 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>');
    
    if (line.trim().startsWith('---')) {
      return <hr key={idx} className="border-white/10 my-3" />;
    }
    const h3Match = line.match(/^###\s+(.+)/);
    if (h3Match) {
      return <p key={idx} className="text-sm font-bold text-white mt-3 mb-1" dangerouslySetInnerHTML={{ __html: h3Match[1].replace(/\*\*(.*?)\*\*/g, '$1') }} />;
    }
    const h2Match = line.match(/^##\s+(.+)/);
    if (h2Match) {
      return <p key={idx} className="text-base font-bold text-white mt-3 mb-1" dangerouslySetInnerHTML={{ __html: h2Match[1].replace(/\*\*(.*?)\*\*/g, '$1') }} />;
    }
    if (line.trim().startsWith('>')) {
      return (
        <div key={idx} className="border-l-2 border-sky-500/40 pl-3 my-1.5 italic text-slate-400">
          <span dangerouslySetInnerHTML={{ __html: html.replace(/^>\s*/, '') }} />
        </div>
      );
    }
    if (line.trim().match(/^[•\-]\s/)) {
      return (
        <div key={idx} className="flex gap-2.5 my-1 pl-1">
          <span className="text-sky-400 mt-0.5 text-xs">&#9679;</span>
          <span className="flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: html.replace(/^[•\-]\s*/, '') }} />
        </div>
      );
    }
    const numMatch = line.match(/^(\d+)\.\s/);
    if (numMatch) {
      return (
        <div key={idx} className="flex gap-2.5 my-1 pl-1">
          <span className="text-sky-400 font-semibold text-xs min-w-[1.2rem] text-right mt-0.5">{numMatch[1]}.</span>
          <span className="flex-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: html.replace(/^\d+\.\s*/, '') }} />
        </div>
      );
    }
    if (!line.trim()) return <div key={idx} className="h-2.5" />;
    return <div key={idx} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />;
  });
};

export const MentorAIPanel = ({ 
  onClose, 
  dayNumber, 
  dayTitle,
  dayContext,
  task = null,
  lessonContext = ''
}) => {
  const { api, user } = useAuth();
  const isPremium = user?.subscription_status === 'active';
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usageInfo, setUsageInfo] = useState({ used: 0, remaining: isPremium ? 99999 : 50, limit: isPremium ? 99999 : 50 });
  const [isBlocked, setIsBlocked] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [inputRows, setInputRows] = useState(1);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const welcome = task 
      ? task.aiStartMessage 
      : buildFreeWelcome();
    setMessages([{ role: 'assistant', content: welcome, ts: Date.now() }]);
    
    // Fetch initial usage
    api.get('/ai-coach/usage').then(res => {
      setUsageInfo(res.data);
      if (res.data.remaining <= 0) setIsBlocked(true);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, dayNumber, dayTitle]);

  const buildFreeWelcome = () => {
    return `**Mentor** · Dzień ${dayNumber}

Temat: **${dayTitle}**

---

Pracujmy razem. Mogę:

- Analizować i ulepszać Twoje prompty
- Porównywać wersje: słaba vs dobra vs premium
- Generować warianty z różnych perspektyw
- Prowadzić Cię przez praktyczne ćwiczenia

Wklej prompt, opisz problem albo zacznij jedno z zadań na dziś.`;
  };

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const copyMessage = async (content, index) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch(e) { /* ignore */ }
  };

  const sendMessage = async (customMessage = null) => {
    const msg = customMessage || input.trim();
    if (!msg || isLoading || isBlocked) return;
    if (!customMessage) {
      setInput('');
      setInputRows(1);
    }
    setError(null);

    const userMsg = { role: 'user', content: msg, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const history = messages
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const taskContext = task ? `Zadanie: ${task.title}\nCel: ${task.goal}\nTryb: ${task.aiMode || 'interactive'}` : null;

      const payload = {
        message: msg,
        conversation_history: history,
        day_number: dayNumber,
        lesson_context: dayContext || dayTitle,
        task_context: taskContext,
        interaction_mode: task?.aiMode || null
      };

      let res;
      try {
        res = await api.post('/ai-coach', payload);
      } catch (firstErr) {
        if (firstErr.response) throw firstErr;
        res = await api.post('/ai-coach', payload);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: res.data.response,
        ts: Date.now()
      }]);
      if (res.data.remaining_uses !== undefined) {
        const newRemaining = res.data.remaining_uses;
        const newUsed = res.data.used || usageInfo.used + 1;
        const newLimit = res.data.limit || usageInfo.limit;
        setUsageInfo({ remaining: newRemaining, used: newUsed, limit: newLimit });
        if (newRemaining <= 0) setIsBlocked(true);
      }
    } catch (e) {
      if (e.response?.status === 429) {
        setIsBlocked(true);
        setError(null);
      } else if (e.response?.status === 403) {
        const detail = e.response?.data?.detail || '';
        if (detail.includes('limit') || detail.includes('Limit')) {
          setIsBlocked(true);
          setError(null);
        } else {
          setError('Wymaga aktywnej subskrypcji Premium.');
        }
      } else {
        setError('Błąd połączenia. Spróbuj ponownie.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    const lines = e.target.value.split('\n').length;
    setInputRows(Math.min(Math.max(lines, 1), 4));
  };

  const userMsgCount = messages.filter(m => m.role === 'user').length;
  const hasUserContent = userMsgCount > 0;

  const getRelevantActions = () => {
    if (isBlocked) return [];
    if (!hasUserContent && task) {
      return ['nextStep'];
    }
    if (!hasUserContent) {
      return [];
    }
    return ['improve', 'variants', 'compare', 'simplify', 'expand', 'nextStep'];
  };

  const relevantActions = getRelevantActions();

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="mentor-ai-panel">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-lg" onClick={onClose} />
      
      <div 
        className="relative w-full sm:w-[640px] sm:max-w-[95vw] h-[92vh] sm:h-[85vh] sm:max-h-[800px] flex flex-col overflow-hidden"
        style={{ 
          background: '#0c1222',
          borderRadius: '20px 20px 0 0',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 -10px 60px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)'
        }}
      >
        {/* HEADER */}
        <div className="flex-shrink-0 relative" style={{ background: 'linear-gradient(180deg, rgba(14,165,233,0.08) 0%, transparent 100%)' }}>
          <div className="px-5 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}>
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0c1222]" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-base tracking-tight" data-testid="mentor-title">Mentor</h3>
                  <p className="text-[11px] text-slate-500 font-medium">Dzień {dayNumber} · {dayTitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isPremium && (
                  <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-lg border border-white/5" data-testid="mentor-usage-counter">
                    <Sparkles className="w-3 h-3 text-sky-400" />
                    <span className="text-[11px] font-semibold text-slate-400">{usageInfo.remaining}/{usageInfo.limit}</span>
                  </div>
                )}
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/5 transition-colors" data-testid="close-mentor-btn">
                  <X className="w-5 h-5 text-slate-500 hover:text-white transition-colors" />
                </button>
              </div>
            </div>
          </div>

          {/* Active task indicator */}
          {task && (
            <div className="mx-5 mb-3 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl" 
              style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.15)' }}
              data-testid="active-task-bar"
            >
              <Target className="w-4 h-4 text-sky-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sky-300 truncate">{task.title.replace(/^[^\s]+\s/, '')}</p>
              </div>
              <span className="text-[10px] font-semibold text-sky-500/60 uppercase tracking-wider flex-shrink-0">aktywne</span>
            </div>
          )}
          
          <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>

        {/* MESSAGES */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5" data-testid="messages-container">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 mt-1" 
                  style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}>
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div className={`max-w-[88%] sm:max-w-[82%] ${msg.role === 'user' ? 'ml-auto' : ''}`}>
                <div className={`rounded-2xl px-4 py-3 ${
                  msg.role === 'assistant'
                    ? 'bg-white/[0.05] border border-white/[0.06]'
                    : 'bg-sky-600 text-white'
                }`} style={msg.role === 'assistant' ? {} : { boxShadow: '0 2px 12px rgba(14,165,233,0.25)' }}>
                  <div className={`text-[13px] ${msg.role === 'assistant' ? 'text-slate-300' : 'text-white'}`}>
                    <FormatMessage content={msg.content} />
                  </div>
                </div>
                
                {msg.role === 'assistant' && idx > 0 && (
                  <div className="flex items-center gap-1 mt-1.5 ml-1">
                    <button
                      onClick={() => copyMessage(msg.content, idx)}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-slate-600 hover:text-slate-400 transition-colors"
                      data-testid={`copy-msg-${idx}`}
                    >
                      {copiedIndex === idx ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => sendMessage('Rozwin te mysl bardziej szczegolowo')}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-slate-600 hover:text-slate-400 transition-colors"
                      data-testid={`expand-msg-${idx}`}
                    >
                      <TrendingUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => sendMessage('Pokaz alternatywne podejscie')}
                      className="p-1.5 rounded-lg hover:bg-white/5 text-slate-600 hover:text-slate-400 transition-colors"
                      data-testid={`alt-msg-${idx}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              
              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0 mt-1">
                  <User className="w-4 h-4 text-slate-400" />
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" 
                style={{ background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)' }}>
                <Loader2 className="w-4 h-4 text-white animate-spin" />
              </div>
              <div className="bg-white/[0.05] border border-white/[0.06] rounded-2xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <span className="text-[13px] text-slate-500">Pracuję nad tym</span>
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse" />
                    <span className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
                    <span className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* ERROR */}
        {error && (
          <div className="flex-shrink-0 mx-4 mb-2 px-4 py-2.5 rounded-xl bg-red-500/10 border border-red-500/15">
            <p className="text-xs text-red-400 font-medium">{error}</p>
          </div>
        )}

        {/* BLOCKED STATE — paywall for free users */}
        {isBlocked && !isPremium && (
          <div className="flex-shrink-0 mx-4 mb-3" data-testid="mentor-paywall">
            <div 
              className="rounded-xl px-5 py-5 text-center"
              style={{
                background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(14,165,233,0.06))',
                border: '1px solid rgba(251,191,36,0.18)',
              }}
            >
              <Crown className="w-6 h-6 text-amber-400 mx-auto mb-2.5" />
              <p className="text-sm font-bold text-white mb-1">Dzienny limit wyczerpany</p>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                W planie Premium rozmawiasz z Mentorem bez ograniczeń — bez dziennego limitu.
              </p>
              <button
                onClick={() => { onClose(); window.location.href = '/paywall'; }}
                className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.3)',
                }}
                data-testid="mentor-upgrade-btn"
              >
                Sprawdź plany Premium
                <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* BLOCKED STATE — limit for premium users (practically never happens) */}
        {isBlocked && isPremium && (
          <div className="flex-shrink-0 mx-4 mb-3">
            <div className="rounded-xl px-4 py-3 bg-white/[0.04] border border-white/[0.06] text-center">
              <p className="text-xs text-slate-400">Dzienny limit wiadomości wykorzystany. Wróć jutro!</p>
            </div>
          </div>
        )}

        {/* QUICK ACTIONS BAR */}
        {relevantActions.length > 0 && !isLoading && (
          <div className="flex-shrink-0 px-4 py-2.5 border-t border-white/[0.04]" data-testid="quick-actions">
            <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide">
              {relevantActions.map((key) => {
                const action = QUICK_ACTIONS[key];
                if (!action) return null;
                const Icon = action.icon;
                return (
                  <button
                    key={key}
                    onClick={() => sendMessage(action.prompt)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-slate-400 hover:text-sky-300 bg-white/[0.03] hover:bg-sky-500/10 border border-white/[0.05] hover:border-sky-500/20 transition-all text-xs font-medium"
                    data-testid={`action-${key}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* INPUT */}
        <div className="flex-shrink-0 p-4 border-t border-white/[0.06]" style={{ background: 'rgba(12,18,34,0.95)' }}>
          <div className="flex gap-2.5 items-end">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={isBlocked ? (isPremium ? "Limit wyczerpany" : "Odblokuj pełny dostęp...") : (task ? "Twoja odpowiedź..." : "Wpisz prompt, pytanie lub pomysł...")}
                rows={inputRows}
                disabled={isBlocked}
                className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:border-sky-500/40 resize-none text-[13px] leading-relaxed transition-colors disabled:opacity-40"
                style={{ minHeight: '46px', maxHeight: '120px' }}
                data-testid="mentor-input"
              />
              {!isBlocked && (
                <div className="absolute right-3 bottom-2.5 flex items-center gap-1">
                  <span className="text-[9px] text-slate-700 font-medium">
                    <CornerDownLeft className="w-3 h-3 inline" /> Wyślij
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading || isBlocked}
              className="h-[46px] w-[46px] rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
              style={{ 
                background: input.trim() && !isLoading && !isBlocked
                  ? 'linear-gradient(135deg, #0ea5e9, #0284c7)' 
                  : 'rgba(255,255,255,0.03)',
                boxShadow: input.trim() && !isLoading && !isBlocked ? '0 2px 12px rgba(14,165,233,0.3)' : 'none'
              }}
              data-testid="send-btn"
            >
              {isBlocked && !isPremium ? (
                <Lock className="w-4 h-4 text-slate-500" />
              ) : (
                <Send className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorAIPanel;
